import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthenticatedPreloadStrategy } from './guards/authenticated-preload.strategy';
import {AuthGuard} from './guards/auth.guard';
import {CustomerContactModule} from "./modules/customer-contact/customer-contact.module";
import {MainLayoutComponent} from "./modules/shared/main-layout/main-layout.component";
import {AccessDeniedComponent} from "./modules/shared/access-denied/access-denied.component";
import {GlobalSearchResultsComponent} from "./modules/shared/global-search-results/global-search-results.component";


const routes: Routes = [
  {
    path: 'auth', loadChildren: () =>
      import('./modules/auth/auth.module').then(
        (module) => module.AuthModule
      ),
  },
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      { path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'dashboard',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/dashboard/dashboard.module').then(
            (module) => module.DashboardModule
          ),
      },
      {
        path: 'customers',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/customer/customer.module').then(
            (module) => module.CustomerModule
          ),
      },
      {
        path: 'customer/contacts',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/customer-contact/customer-contact.module').then(
            (module) => module.CustomerContactModule
          ),
      },
     
      {
        path: 'bids',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/sale/bid/bid.module').then(
            (module) => module.BidModule
          ),
      },
      {
        path: 'proposals',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/sale/proposal/proposal.module').then(
            (module) => module.ProposalModule
          ),
      },
      {
        path: 'takeoffs',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/sale/take-offs/take-offs.module').then(
            (module) => module.TakeOffsModule
          ),
      },
      {
        path: 'jobs',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/sale/job/job.module').then(
            (module) => module.JobModule
          ),
      },
      {
        path: 'tasks',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/sale/task/task.module').then(
            (module) => module.TaskModule
          ),
      },
      {
        path: 'tasks-templates',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/task/task-templates/task-templates.module').then(
            (module) => module.TaskTemplatesModule
          ),
      },
      {
        path: 'milestones',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/milestones/milestones.module').then(
            (module) => module.MilestonesModule
          ),
      },
      {
        path: 'vendors',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/inventory/vendor/vendor.module').then(
            (module) => module.VendorModule
          ),
      },

      {
        path: 'categories',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/inventory/category/category.module').then(
            (module) => module.CategoryModule
          ),
      },

      {
        path: 'warehouses',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/inventory/warehouse/warehouse.module').then(
            (module) => module.WarehouseModule
          ),
      },

      {
        path: 'products',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/inventory/product/product.module').then(
            (module) => module.ProductModule
          ),
      },

      {
        path: 'subcontractors',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/inventory/subcontractor/subcontractor.module').then(
            (module) => module.SubcontractorModule
          ),
      },

      {
        path: 'services',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/inventory/service/service.module').then(
            (module) => module.ServiceModule
          ),
      },

      {
        path: 'jobsites',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/jobsite/jobsite.module').then(
            (module) => module.JobsiteModule
          ),
      },

      {
        path: 'purchase-order',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/purchase-order/purchase-order.module').then(
            (module) => module.PurchaseOrderModule
          ),
      },

      {
        path: 'accounting',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/accounting/accounting.module').then(
            (module) => module.AccountingModule
          ),
        // NOTE: lazy-loaded; the spec marks eager-load conversion as a Phase 6 optimization.
      },

      {
        path: 'settings',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/setting/global-setting/global-setting.module').then(
            (module) => module.GlobalSettingModule
          ),
      },

      {
        path: 'submittals',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/sale/submittal/submittal.module').then(
            (module) => module.SubmittalModule
          ),
      },
      {
        path: 'notes',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/note/note.module').then(
            (module) => module.NoteModule
          ),
      },
      {
        path: 'tickets',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/ticket/ticket.module').then(
            (module) => module.TicketModule
          ),
      },
      {
        path: 'ticket-templates',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/ticket-templates/ticket-templates.module').then(
            (module) => module.TicketTemplatesModule
          ),
      },
      {
        path: 'change-orders',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/sale/change-order/change-order.module').then(
            (module) => module.ChangeOrderModule
          ),
      },
      {
        path: 'change-order',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/awarded-jobs/change-order/change-order.module').then(
            (module) => module.ChangeOrderModule
          ),
      },
      {
        path: 'hr',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/hr/hr.module').then(
            (module) => module.HRModule
          ),
      },
      {
        path: 'teams',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/teams/teams.module').then(
            (module) => module.TeamsModule
          ),
      },
      {

        path: 'calendar',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/event-calendar/event-calendar.module').then(
            (module) => module.EventCalendarModule
          ),
      },
      {
        path: 'email',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/email/email.module').then(
            (module) => module.EmailModule
          ),
      },
      {
        path: 'document',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/document/document.module').then(
            (module) => module.DocumentModule

          ),
      },
      {
        path: 'syncfusion-test',
        loadChildren: () =>
          import('./modules/syncfusion-test/syncfusion-test.module').then(
            (module) => module.SyncfusionTestModule
          ),
      },
      {
        path: 'workflows',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/workflows/workflows.module').then(
            (module) => module.WorkflowsModule
          ),
      },

      {
        // Global Search "View all" — paginated results for one module (backend GET /search/{module})
        path: 'search/:module',
        canActivate: [AuthGuard],
        component: GlobalSearchResultsComponent,
      },

      {
        path: 'access-denied',
        canActivate: [AuthGuard],
        component: AccessDeniedComponent,
      }

    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    // Not PreloadAllModules: that pulled every feature chunk down behind the login screen.
    preloadingStrategy: AuthenticatedPreloadStrategy
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
